import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Select, Button, Space, Typography, message, Table, DatePicker, Statistic, Row, Col, Checkbox, Tabs } from 'antd';
import { UsergroupAddOutlined, DownloadOutlined, FileTextOutlined, TableOutlined, BarChartOutlined, ApartmentOutlined } from '@ant-design/icons';
import { fetchVacancies } from '../../redux/vacanciesSlice';
import { fetchUsersByOrganisations } from '../../redux/userOrganisationsSlice';
import { fetchDepartmentsByOrganisation } from '../../redux/organisationDepartmentSlice';
import { fetchRecruiterReport, exportRecruiterReportToExcel } from '../../redux/analyticsSlice';
import moment from 'moment';
import 'moment/locale/ru';
import locale from 'antd/es/date-picker/locale/ru_RU';

// Устанавливаем русскую локаль для moment
moment.locale('ru');

const { Title, Text } = Typography;
const { Option } = Select;

// Объект соответствия английских названий месяцев русским
const MONTH_NAMES_MAP = {
  "January": "Январь",
  "February": "Февраль",
  "March": "Март",
  "April": "Апрель",
  "May": "Май",
  "June": "Июнь",
  "July": "Июль",
  "August": "Август",
  "September": "Сентябрь",
  "October": "Октябрь",
  "November": "Ноябрь",
  "December": "Декабрь"
};

const STATUS_LABELS = {
  new: 'Новая',
  in_progress: 'В работе',
  stopped: 'Приостановлена',
  hanging: 'Зависшая',
  closed: 'Закрыта',
  template: 'Черновик'
};

const RecruiterReportsPage = () => {
  const dispatch = useDispatch();
  const { orgId, role, userOrganisations } = useSelector((state) => state.auth);
  const { organisations, allDepartments } = useSelector((state) => state.organisationDepartment);
  const { organisationUsers: users, isLoading: usersLoading } = useSelector((state) => state.userOrganisations);
  const { isLoading: reportLoading, isExporting } = useSelector((state) => state.analytics);

  // Функция для форматирования месяца на русском языке
  const formatMonthYear = (date) => {
    if (!date) return '';
    try {

      // Форматируем дату (может быть на английском)
      const formatted = date.format('MMMM YYYY');

      // Извлекаем название месяца и год
      const parts = formatted.split(' ');
      const monthName = parts[0];
      const year = parts[1];

      // Заменяем английское название месяца на русское
      const russianMonth = MONTH_NAMES_MAP[monthName] || monthName;
      return `${russianMonth} ${year}`;
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return '';
    }
  };

  // Состояния фильтров
  const [selectedOrganisations, setSelectedOrganisations] = useState([]);
  const [selectAllOrgs, setSelectAllOrgs] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectAllDepts, setSelectAllDepts] = useState(false);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  const [selectAllRecruiters, setSelectAllRecruiters] = useState(false);
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [reportData, setReportData] = useState(null);

  const debounceTimerRef = useRef(null);

  const availableOrganisations = useMemo(() => {
    if (role === 'superadmin') {
      return organisations;
    }
    return organisations.filter(org =>
      userOrganisations.some(userOrg =>
        (userOrg.organisation_id === org.id || userOrg.organisation_id === org.organisation_id)
      )
    );
  }, [role, organisations, userOrganisations]);

  useEffect(() => {
    if (orgId && availableOrganisations.length > 0 && selectedOrganisations.length === 0) {
      const orgIdNum = Number(orgId);
      const currentOrg = availableOrganisations.find(o => {
        const orgIdValue = Number(o.organisation_id || o.id);
        const orgIdValueAlt = Number(o.id || o.organisation_id);
        return orgIdValue === orgIdNum || orgIdValueAlt === orgIdNum ||
          String(o.id) === String(orgId) || String(o.organisation_id) === String(orgId) ||
          o.id === orgId || o.organisation_id === orgId;
      });
      if (currentOrg) {
        // Используем то же значение, которое используется в Option value (organisation_id имеет приоритет)
        const orgIdToSet = currentOrg.organisation_id || currentOrg.id;
        setSelectedOrganisations([orgIdToSet]);
      }
    }
  }, [orgId, availableOrganisations, selectedOrganisations.length]);

  const availableDepartments = useMemo(() => {
    if (selectAllOrgs) {
      return availableOrganisations.reduce((acc, org) => {
        const depts = org.departments || [];
        return [...acc, ...depts];
      }, []);
    }

    return selectedOrganisations.reduce((acc, orgId) => {
      const org = availableOrganisations.find(o => (o.id === orgId || o.organisation_id === orgId));
      const depts = org?.departments || [];
      return [...acc, ...depts];
    }, []);
  }, [selectedOrganisations, selectAllOrgs, availableOrganisations]);

  // Получение рекрутеров из выбранных организаций
  // Рекрутеры приходят через API /user_organisations/users?org_ids=...
  // который возвращает всех пользователей из указанных организаций
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (selectedOrganisations.length === 0 && !selectAllOrgs) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      let orgIds = [];

      if (selectAllOrgs) {
        // Нормализуем ID - используем organisation_id если есть, иначе id
        orgIds = availableOrganisations.map(org => org.organisation_id || org.id);
      } else {
        // Нормализуем ID из selectedOrganisations
        orgIds = selectedOrganisations.map(orgId => {
          const org = availableOrganisations.find(o =>
            (o.id === orgId || o.organisation_id === orgId) ||
            (String(o.id) === String(orgId) || String(o.organisation_id) === String(orgId))
          );
          return org ? (org.organisation_id || org.id) : orgId;
        });
      }

      if (orgIds.length > 0) {
        // Загружаем всех пользователей из выбранных организаций
        dispatch(fetchUsersByOrganisations(orgIds));
      }
    }, 1500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [dispatch, selectedOrganisations, selectAllOrgs, availableOrganisations]);

  useEffect(() => {
    if (selectAllOrgs) {
      availableOrganisations.forEach(org => {
        if (!org.departments || org.departments.length === 0) {
          dispatch(fetchDepartmentsByOrganisation({ organisationId: org.id || org.organisation_id }));
        }
      });
    } else if (selectedOrganisations.length > 0) {
      selectedOrganisations.forEach(orgId => {
        const org = availableOrganisations.find(o => (o.id === orgId || o.organisation_id === orgId));
        if (org && (!org.departments || org.departments.length === 0)) {
          dispatch(fetchDepartmentsByOrganisation({ organisationId: orgId }));
        }
      });
    }
  }, [dispatch, selectedOrganisations, selectAllOrgs, availableOrganisations]);

  // Фильтруем рекрутеров из загруженных пользователей
  // Рекрутеры - это пользователи с ролью 'hr' или 'admin' из выбранных организаций
  const recruiters = useMemo(() => {
    if (!users || users.length === 0) return [];

    return users.filter(user =>
      user.role === 'hr' || user.role === 'admin'
    );
  }, [users]);

  const handleSelectAllOrgs = (checked) => {
    setSelectAllOrgs(checked);
    if (checked) {
      const allOrgIds = availableOrganisations.map(org => org.id || org.organisation_id);
      setSelectedOrganisations(allOrgIds);
    } else {
      setSelectedOrganisations([]);
    }
    setSelectedDepartments([]);
    setSelectAllDepts(false);
    setSelectedRecruiters([]);
    setSelectAllRecruiters(false);
  };

  const handleOrganisationsChange = (values) => {
    setSelectedOrganisations(values);
    setSelectAllOrgs(values.length === availableOrganisations.length);
    setSelectedDepartments([]);
    setSelectAllDepts(false);
    setSelectedRecruiters([]);
    setSelectAllRecruiters(false);
  };

  const handleSelectAllDepts = (checked) => {
    setSelectAllDepts(checked);
    if (checked) {
      const allDeptIds = availableDepartments.map(dept => dept.id || dept.department_id);
      setSelectedDepartments(allDeptIds);
    } else {
      setSelectedDepartments([]);
    }
  };

  const handleDepartmentsChange = (values) => {
    setSelectedDepartments(values);
    setSelectAllDepts(values.length === availableDepartments.length);
  };

  const handleSelectAllRecruiters = (checked) => {
    setSelectAllRecruiters(checked);
    if (checked) {
      const allRecruiterIds = recruiters.map(r => r.user_id);
      setSelectedRecruiters(allRecruiterIds);
    } else {
      setSelectedRecruiters([]);
    }
  };

  const handleRecruitersChange = (values) => {
    setSelectedRecruiters(values);
    setSelectAllRecruiters(values.length === recruiters.length);
  };

  const handleGenerateReport = async () => {
    if (!periodStart || !periodEnd) {
      message.warning('Пожалуйста, выберите период');
      return;
    }

    if (selectedOrganisations.length === 0 && !selectAllOrgs) {
      message.warning('Пожалуйста, выберите хотя бы одну организацию');
      return;
    }

    try {
      const params = {
        period_start: periodStart.format('YYYY-MM-DD'),
        period_end: periodEnd.format('YYYY-MM-DD'),
      };

      if (!selectAllOrgs && selectedOrganisations.length > 0) {
        // Нормализуем ID организаций - используем organisation_id если есть, иначе id
        params.org_ids = selectedOrganisations.map(orgId => {
          const org = availableOrganisations.find(o =>
            (o.id === orgId || o.organisation_id === orgId) ||
            (String(o.id) === String(orgId) || String(o.organisation_id) === String(orgId))
          );
          return org ? (org.organisation_id || org.id) : orgId;
        });
      } else if (selectAllOrgs) {
        // Если выбраны все организации, отправляем все доступные org_ids
        params.org_ids = availableOrganisations.map(org => org.organisation_id || org.id);
      }

      if (!selectAllDepts && selectedDepartments.length > 0) {
        params.department_ids = selectedDepartments;
      }


      if (selectAllRecruiters) {
        params.recruiter_ids = recruiters.map(r => r.user_id);
      } else if (selectedRecruiters.length > 0) {
        params.recruiter_ids = selectedRecruiters;
      }

      const apiReport = await dispatch(fetchRecruiterReport(params)).unwrap();

      const report = {
        ...apiReport,
        generatedAt: moment().format('DD.MM.YYYY HH:mm'),
        filters: {
          organisations: selectAllOrgs ? 'Все' : selectedOrganisations.map(id =>
            availableOrganisations.find(o => (o.id === id || o.organisation_id === id))?.name
          ).join(', '),
          departments: selectAllDepts ? 'Все' : selectedDepartments.map(id =>
            availableDepartments.find(d => (d.id === id || d.department_id === id))?.name
          ).join(', '),
          recruiters: selectAllRecruiters ? 'Все' : selectedRecruiters.map(id =>
            recruiters.find(r => r.user_id === id)?.first_name + ' ' + recruiters.find(r => r.user_id === id)?.last_name
          ).join(', '),
          period: `${formatMonthYear(periodStart)}${periodStart.format('YYYY-MM') !== periodEnd.format('YYYY-MM') ? ' - ' + formatMonthYear(periodEnd) : ''}`
        }
      };

      setReportData(report);
      message.success('Отчет успешно сгенерирован');
    } catch (error) {
      message.error('Ошибка при генерации отчета');
      console.error(error);
    }
  };

  const handleReset = () => {
    setSelectedOrganisations([]);
    setSelectAllOrgs(false);
    setSelectedDepartments([]);
    setSelectAllDepts(false);
    setSelectedRecruiters([]);
    setSelectAllRecruiters(false);
    setPeriodStart(null);
    setPeriodEnd(null);
    setReportData(null);
  };

  const handleExportToExcel = async () => {
    if (!periodStart || !periodEnd) {
      message.warning('Пожалуйста, выберите период');
      return;
    }

    try {
      const params = {
        period_start: periodStart.format('YYYY-MM-DD'),
        period_end: periodEnd.format('YYYY-MM-DD'),
      };

      if (!selectAllOrgs && selectedOrganisations.length > 0) {
        // Нормализуем ID организаций - используем organisation_id если есть, иначе id
        params.org_ids = selectedOrganisations.map(orgId => {
          const org = availableOrganisations.find(o =>
            (o.id === orgId || o.organisation_id === orgId) ||
            (String(o.id) === String(orgId) || String(o.organisation_id) === String(orgId))
          );
          return org ? (org.organisation_id || org.id) : orgId;
        });
      } else if (selectAllOrgs) {
        // Если выбраны все организации, отправляем все доступные org_ids
        params.org_ids = availableOrganisations.map(org => org.organisation_id || org.id);
      }

      if (!selectAllDepts && selectedDepartments.length > 0) {
        params.department_ids = selectedDepartments;
      }

      // Отправляем id рекрутеров: либо всех, либо выбранных
      if (selectAllRecruiters) {
        // Если выбрано "Выбрать всех", отправляем id всех рекрутеров из выбранных организаций
        params.recruiter_ids = recruiters.map(r => r.user_id);
      } else if (selectedRecruiters.length > 0) {
        // Если выбраны конкретные рекрутеры, отправляем их id
        params.recruiter_ids = selectedRecruiters;
      }

      await dispatch(exportRecruiterReportToExcel(params)).unwrap();
      message.success('Отчет успешно экспортирован');
    } catch (error) {
      message.error('Ошибка при экспорте отчета');
      console.error(error);
    }
  };

  // Колонки для подробного отчета
  const detailedColumns = [
    {
      title: 'ID вакансии',
      dataIndex: 'vacancy_id',
      key: 'vacancy_id',
      width: 100,
    },
    {
      title: 'Должность',
      dataIndex: 'job_title',
      key: 'job_title',
      width: 200,
    },
    {
      title: 'Компания',
      dataIndex: 'organisation_name',
      key: 'organisation_name',
      width: 150,
    },
    {
      title: 'Отдел',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 150,
    },
    {
      title: 'Рекрутер',
      dataIndex: 'recruiter_name',
      key: 'recruiter_name',
      width: 150,
      render: (name) => name || '-',
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
      width: 120,
    },
    {
      title: 'Дата открытия',
      dataIndex: 'open_date',
      key: 'open_date',
      width: 120,
      render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'План. дата закрытия',
      dataIndex: 'planned_close_date',
      key: 'planned_close_date',
      width: 140,
      render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Фактическая дата закрытия',
      dataIndex: 'close_date',
      key: 'close_date',
      width: 120,
      render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Статус',
      dataIndex: 'status_group',
      key: 'status_group',
      width: 120,
      fixed: 'right',
    },
  ];

  // Колонки для краткого отчета по рекрутерам
  const recruiterSummaryColumns = [
    {
      title: 'Рекрутер',
      dataIndex: 'recruiter',
      key: 'recruiter',
      render: (name) => name || 'Не назначен',
      width: 200,
    },
    {
      title: 'Компания',
      dataIndex: 'company',
      key: 'company',
      width: 200,
    },
    {
      title: 'Отдел',
      dataIndex: 'department',
      key: 'department',
      width: 200,
    },
    {
      title: 'Всего',
      dataIndex: 'total',
      key: 'total',
      width: 80,
      align: 'center',
    },
    {
      title: 'Открытые',
      dataIndex: 'Открытые',
      key: 'open',
      width: 100,
      align: 'center',
    },
    {
      title: 'Приостановлены',
      dataIndex: 'Приостановлены',
      key: 'stopped',
      width: 120,
      align: 'center',
    },
    {
      title: 'Зависшие',
      dataIndex: 'Зависшие',
      key: 'hanging',
      width: 100,
      align: 'center',
    },
    {
      title: 'Закрытые',
      dataIndex: 'Закрытые',
      key: 'closed',
      width: 100,
      align: 'center',
    },
  ];

  // Колонки для краткого отчета по компаниям
  const companySummaryColumns = [
    {
      title: 'Компания',
      dataIndex: 'company',
      key: 'company',
      width: 200,
    },
    {
      title: 'Отдел',
      dataIndex: 'department',
      key: 'department',
      width: 200,
    },
    {
      title: 'Всего',
      dataIndex: 'total',
      key: 'total',
      width: 80,
      align: 'center',
    },
    {
      title: 'Открытые',
      dataIndex: 'Открытые',
      key: 'open',
      width: 100,
      align: 'center',
    },
    {
      title: 'Приостановлены',
      dataIndex: 'Приостановлены',
      key: 'stopped',
      width: 120,
      align: 'center',
    },
    {
      title: 'Зависшие',
      dataIndex: 'Зависшие',
      key: 'hanging',
      width: 100,
      align: 'center',
    },
    {
      title: 'Закрытые',
      dataIndex: 'Закрытые',
      key: 'closed',
      width: 100,
      align: 'center',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <UsergroupAddOutlined style={{ fontSize: '24px', marginRight: '12px' }} />
            <Title level={3} style={{ display: 'inline' }}>
              Отчетность по рекрутерам
            </Title>
          </div>

          {/* Фильтры */}
          <Card title="Фильтры" size="small">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Организации */}
              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Организации: *</Text>
                    <Checkbox checked={selectAllOrgs} onChange={(e) => handleSelectAllOrgs(e.target.checked)}>
                      Выбрать все
                    </Checkbox>
                  </Space>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Выберите организации"
                    value={selectedOrganisations}
                    onChange={handleOrganisationsChange}
                    disabled={selectAllOrgs}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {availableOrganisations.map((org) => {
                      const orgValue = org.organisation_id || org.id;
                      return (
                        <Option key={orgValue} value={orgValue}>
                          {org.name}
                        </Option>
                      );
                    })}
                  </Select>
                </Space>
              </div>

              {/* Отделы */}
              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Отделы:</Text>
                    <Checkbox
                      checked={selectAllDepts}
                      onChange={(e) => handleSelectAllDepts(e.target.checked)}
                      disabled={selectedOrganisations.length === 0 && !selectAllOrgs}
                    >
                      Выбрать все
                    </Checkbox>
                  </Space>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Выберите отделы (необязательно)"
                    value={selectedDepartments}
                    onChange={handleDepartmentsChange}
                    disabled={selectAllDepts || (selectedOrganisations.length === 0 && !selectAllOrgs)}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {availableDepartments.map((dept) => (
                      <Option key={dept.id || dept.department_id} value={dept.id || dept.department_id}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </div>

              {/* Рекрутеры */}
              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Рекрутеры:</Text>
                    <Checkbox
                      checked={selectAllRecruiters}
                      onChange={(e) => handleSelectAllRecruiters(e.target.checked)}
                      disabled={selectedOrganisations.length === 0 && !selectAllOrgs}
                    >
                      Выбрать всех
                    </Checkbox>
                  </Space>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Выберите рекрутеров (необязательно)"
                    value={selectedRecruiters}
                    onChange={handleRecruitersChange}
                    loading={usersLoading}
                    disabled={selectAllRecruiters || (selectedOrganisations.length === 0 && !selectAllOrgs)}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {recruiters.map((user) => (
                      <Option key={user.id} value={user.user_id}>
                        {user.first_name} {user.last_name}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </div>

              {/* Период */}
              <div>
                <Text strong>Период отчета: *</Text>
                <Space style={{ width: '100%', marginTop: '8px' }}>
                  <DatePicker
                    picker="month"
                    locale={locale}
                    placeholder="Начало периода"
                    value={periodStart}
                    onChange={(date) => {
                      setPeriodStart(date);
                    }}
                    format={formatMonthYear}
                    style={{ width: '200px' }}
                  />
                  <Text>—</Text>
                  <DatePicker
                    picker="month"
                    locale={locale}
                    placeholder="Конец периода"
                    value={periodEnd}
                    onChange={(date) => {
                      setPeriodEnd(date);
                    }}
                    format={formatMonthYear}
                    style={{ width: '200px' }}
                    disabledDate={(current) => periodStart && current && current < periodStart}
                  />
                </Space>
              </div>
            </Space>
          </Card>

          {/* Кнопки действий */}
          <Space>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleGenerateReport}
              loading={reportLoading}
              disabled={!periodStart || !periodEnd || (selectedOrganisations.length === 0 && !selectAllOrgs)}
            >
              Сгенерировать отчет
            </Button>
            <Button onClick={handleReset}>
              Сбросить фильтры
            </Button>
          </Space>

          {/* Результат отчета */}
          {reportData && reportData.data && (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Информация о фильтрах */}
              <Card
                title={
                  <Space>
                    <FileTextOutlined />
                    <span>Параметры отчета</span>
                  </Space>
                }
                size="small"
                extra={
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportToExcel}
                    loading={isExporting}
                  >
                    Экспорт в Excel
                  </Button>
                }
              >
                <Space direction="vertical" size="small">
                  <Text><strong>Организации:</strong> {reportData.filters.organisations || 'Все'}</Text>
                  {reportData.filters.departments && (
                    <Text><strong>Отделы:</strong> {reportData.filters.departments || 'Все'}</Text>
                  )}
                  {reportData.filters.recruiters && (
                    <Text><strong>Рекрутеры:</strong> {reportData.filters.recruiters || 'Все'}</Text>
                  )}
                  <Text><strong>Период:</strong> {reportData.filters.period}</Text>
                  <Text type="secondary">
                    Дата формирования отчета: {reportData.generatedAt}
                  </Text>
                </Space>
              </Card>

              {/* Табы с отчетами */}
              <Card>
                <Tabs
                  defaultActiveKey="detailed"
                  items={[
                    {
                      key: 'detailed',
                      label: (
                        <span>
                          <TableOutlined /> Подробный отчет
                        </span>
                      ),
                      children: (
                        <Table
                          columns={detailedColumns}
                          dataSource={reportData.data.detailed}
                          rowKey="vacancy_id"
                          pagination={{
                            pageSize: 20,
                            showTotal: (total) => `Всего: ${total} вакансий`,
                          }}
                          size="small"
                          bordered
                          scroll={{ x: 1500 }}
                          expandable={{
                            expandedRowRender: (record) => (
                              <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                                <Text strong>Воронка кандидатов:</Text>
                                <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                                  {Object.entries(record.funnel || {}).map(([stage, count]) => (
                                    <Col key={stage} xs={12} sm={8} md={6} lg={4}>
                                      <Statistic
                                        title={stage}
                                        value={count}
                                        valueStyle={{ fontSize: '16px' }}
                                      />
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            ),
                            rowExpandable: (record) => record.funnel && Object.keys(record.funnel).length > 0,
                          }}
                        />
                      ),
                    },
                    {
                      key: 'by_recruiter',
                      label: (
                        <span>
                          <BarChartOutlined /> Краткий отчет по рекрутеру
                        </span>
                      ),
                      children: (
                        <Table
                          columns={recruiterSummaryColumns}
                          dataSource={reportData.data.summary_by_recruiters}
                          rowKey={(record, index) => `recruiter-${index}`}
                          pagination={{
                            pageSize: 20,
                            showTotal: (total) => `Всего записей: ${total}`,
                          }}
                          size="small"
                          bordered
                          summary={(pageData) => {
                            let totalCount = 0;
                            let totalOpen = 0;
                            let totalStopped = 0;
                            let totalHanging = 0;
                            let totalClosed = 0;

                            pageData.forEach(({ total, Открытые, Приостановлены, Зависшие, Закрытые }) => {
                              totalCount += total || 0;
                              totalOpen += Открытые || 0;
                              totalStopped += Приостановлены || 0;
                              totalHanging += Зависшие || 0;
                              totalClosed += Закрытые || 0;
                            });

                            return (
                              <Table.Summary fixed>
                                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                                  <Table.Summary.Cell index={0} colSpan={3}>
                                    <strong>Итого на странице:</strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={3} align="center">
                                    {totalCount}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={4} align="center">
                                    {totalOpen}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={5} align="center">
                                    {totalStopped}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={6} align="center">
                                    {totalHanging}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={7} align="center">
                                    {totalClosed}
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                              </Table.Summary>
                            );
                          }}
                        />
                      ),
                    },
                    {
                      key: 'by_company',
                      label: (
                        <span>
                          <ApartmentOutlined /> Краткий отчет по компании
                        </span>
                      ),
                      children: (
                        <Table
                          columns={companySummaryColumns}
                          dataSource={reportData.data.summary_by_companies}
                          rowKey={(record, index) => `company-${index}`}
                          pagination={{
                            pageSize: 20,
                            showTotal: (total) => `Всего записей: ${total}`,
                          }}
                          size="small"
                          bordered
                          summary={(pageData) => {
                            let totalCount = 0;
                            let totalOpen = 0;
                            let totalStopped = 0;
                            let totalHanging = 0;
                            let totalClosed = 0;

                            pageData.forEach(({ total, Открытые, Приостановлены, Зависшие, Закрытые }) => {
                              totalCount += total || 0;
                              totalOpen += Открытые || 0;
                              totalStopped += Приостановлены || 0;
                              totalHanging += Зависшие || 0;
                              totalClosed += Закрытые || 0;
                            });

                            return (
                              <Table.Summary fixed>
                                <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                                  <Table.Summary.Cell index={0} colSpan={2}>
                                    <strong>Итого на странице:</strong>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={2} align="center">
                                    {totalCount}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={3} align="center">
                                    {totalOpen}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={4} align="center">
                                    {totalStopped}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={5} align="center">
                                    {totalHanging}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={6} align="center">
                                    {totalClosed}
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                              </Table.Summary>
                            );
                          }}
                        />
                      ),
                    },
                  ]}
                />
              </Card>
            </Space>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default RecruiterReportsPage;
